/**
 * TracesTab.tsx — the "Rastros" (traces) tab (Fowler's design doc, "MLflow
 * design", Slice 6). List of MLflow traces newest first; expanding a trace
 * shows its spans, ordered by `started_at`, each with a name, kind badge,
 * duration, status badge, and an attributes key/value table. No waterfall
 * chart (out of scope for this slice) — a simple indented list is enough.
 *
 * Three distinct non-happy states (S6 dispatch brief):
 *   - 503 `mlflow_not_configured` → calm "not configured" message, NO retry
 *     button (nothing a retry would fix).
 *   - 502 / transport → error message WITH retry.
 *   - `total: 0` → empty state.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import type { TraceItem, TraceSpan } from '../../api/types';
import { Badge, type BadgeTone } from '../../components/Badge';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { formatDateTime } from '../leads/formatDateTime';
import { useConversationTraces } from './useConversationTraces';

// MLflow status strings aren't a closed enum on our side (upstream cargo) —
// tone by case-insensitive match, neutral fallback for anything else.
const statusTone = (status: string): BadgeTone => {
  const normalized = status.toLowerCase();
  if (normalized === 'ok' || normalized === 'success' || normalized === 'succeeded') return 'ok';
  if (normalized === 'error' || normalized === 'failed') return 'down';
  return 'neutral';
};

const TraceBadge = ({
  tone,
  label,
  testid,
}: {
  tone: BadgeTone;
  label: string;
  testid: string;
}) => (
  <Badge tone={tone} data-testid={testid}>
    {label}
  </Badge>
);

const AttributesTable = ({ attributes }: { attributes: Record<string, string> }) => {
  const { t } = useTranslation();
  const entries = Object.entries(attributes);
  if (entries.length === 0) return null;
  return (
    <table data-testid="trace-span-attributes" className="w-full text-meta text-ink-2">
      <caption className="sr-only">{t('conversations.traces.attributesCaption')}</caption>
      <tbody>
        {entries.map(([key, value]) => (
          <tr key={key} className="align-top">
            <th
              scope="row"
              className="whitespace-nowrap pr-3 py-0.5 text-left font-medium text-ink-3"
            >
              {key}
            </th>
            <td className="whitespace-pre-wrap py-0.5">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SpanRow = ({ span, locale }: { span: TraceSpan; locale: string }) => {
  const { t } = useTranslation();
  return (
    <li
      data-testid="trace-span-row"
      className="flex flex-col gap-1 border-l-2 border-ink-3/20 py-2 pl-4"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="trace-span-name" className="text-body font-medium text-ink-1">
          {span.name}
        </span>
        <TraceBadge tone="neutral" label={span.kind} testid="trace-span-kind-badge" />
        <TraceBadge
          tone={statusTone(span.status)}
          label={span.status}
          testid="trace-span-status-badge"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-meta text-ink-3">
        <time dateTime={span.started_at}>{formatDateTime(span.started_at, locale)}</time>
        <span>{t('conversations.traces.durationMs', { ms: span.duration_ms })}</span>
      </div>
      <AttributesTable attributes={span.attributes} />
    </li>
  );
};

const TraceRow = ({ trace, locale }: { trace: TraceItem; locale: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const sortedSpans = [...trace.spans].sort((a, b) => a.started_at.localeCompare(b.started_at));

  return (
    <li
      data-testid="trace-row"
      className="flex flex-col gap-2 rounded-lg border border-ink-3/10 px-4 py-3"
    >
      <button
        type="button"
        data-testid="trace-row-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        className="flex flex-wrap items-center gap-3 text-left focus:outline-none focus:ring-2 focus:ring-outline-focus"
      >
        <span aria-hidden="true" className="text-ink-3">
          {expanded ? '▾' : '▸'}
        </span>
        <time
          dateTime={trace.started_at}
          data-testid="trace-row-time"
          className="text-body text-ink-1"
        >
          {formatDateTime(trace.started_at, locale)}
        </time>
        <span data-testid="trace-row-duration" className="text-meta text-ink-3">
          {t('conversations.traces.durationMs', { ms: trace.duration_ms })}
        </span>
        <TraceBadge
          tone={statusTone(trace.status)}
          label={trace.status}
          testid="trace-row-status-badge"
        />
        <span data-testid="trace-row-span-count" className="text-meta text-ink-3">
          {t('conversations.traces.spanCount', { count: trace.spans.length })}
        </span>
      </button>
      {expanded ? (
        <ol data-testid="trace-span-list" className="flex flex-col gap-1">
          {sortedSpans.map((span) => (
            <SpanRow key={`${span.name}-${span.started_at}`} span={span} locale={locale} />
          ))}
        </ol>
      ) : null}
    </li>
  );
};

export interface TracesTabProps {
  apiClient: ApiClient;
  threadId: string;
}

export const TracesTab = ({ apiClient, threadId }: TracesTabProps) => {
  const { t, i18n } = useTranslation();
  const { traces, phase, refetch } = useConversationTraces(apiClient, threadId);

  const onRetry = useCallback(() => refetch(), [refetch]);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  if (phase === 'loading' && traces.length === 0) {
    return (
      <p role="status" data-testid="traces-loading" className="text-body text-ink-2">
        {t('conversations.traces.loading')}
      </p>
    );
  }

  if (phase === 'notConfigured') {
    return (
      <p data-testid="traces-not-configured" className="text-body text-ink-3">
        {t('conversations.traces.notConfigured')}
      </p>
    );
  }

  if (phase === 'error') {
    return (
      <div className="flex flex-col items-start gap-3" data-testid="traces-error">
        <p role="alert" className="text-body text-status-down">
          {t('conversations.traces.errorBody')}
        </p>
        <button
          type="button"
          data-testid="traces-retry"
          onClick={onRetry}
          className="inline-flex items-center rounded-lg bg-surface-2 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-1"
        >
          {t('conversations.traces.retry')}
        </button>
      </div>
    );
  }

  if (traces.length === 0) {
    return (
      <p data-testid="traces-empty" className="text-body text-ink-3">
        {t('conversations.traces.empty')}
      </p>
    );
  }

  // Newest first — sort client-side rather than trust upstream ordering.
  const sortedTraces = [...traces].sort((a, b) => b.started_at.localeCompare(a.started_at));

  return (
    <ol data-testid="trace-list" className="flex flex-col gap-3">
      {sortedTraces.map((trace) => (
        <TraceRow key={trace.trace_id} trace={trace} locale={i18n.language} />
      ))}
    </ol>
  );
};
